// Stromový checkbox výběr nad uloženými skupinami stanic — filtr „Skupina" ve výběru 1 stanice.
// trees = [{ id, name, tree }] z useSavedGroupTrees; strom má uzly { label, ghIds: Set, children }.
//
// selected = pole cest, každá cesta = [savedGroupId, bucketLabel, bucketLabel, ...]:
//   [gid]                      = celá uložená skupina
//   [gid, 'Kraj']              = uzel „Kraj" v té skupině (+ všichni potomci)
// Drží se jen „nejhlubší vybraný" bod větve — zaškrtnutí uzlu odstraní nadřazené/podřazené cesty
// téže větve, aby se sjednocení v resolveGroupPaths nezdvojovalo.
import { useState, useRef, useEffect } from 'react'

// Je cesta `path` pokrytá výběrem (ona sama, nebo některý její předek je v selected)?
function isCovered(path, selected) {
  return selected.some(sel =>
    sel.length <= path.length && sel.every((v, i) => v === path[i])
  )
}

// Kolik přímých potomků `path` (o 1 delších) je pokryto?
function countCoveredChildren(childPaths, selected) {
  return childPaths.filter(cp => isCovered(cp, selected)).length
}

function GroupNode({ label, node, path, childrenNodes, selected, onChange, defaultExpanded = false }) {
  const [expanded, setExpanded] = useState(defaultExpanded)
  const checkboxRef = useRef(null)
  const hasChildren = childrenNodes && childrenNodes.length > 0

  const checked = isCovered(path, selected)
  const childPaths = hasChildren ? childrenNodes.map(c => [...path, c.label]) : []
  const coveredChildren = hasChildren ? countCoveredChildren(childPaths, selected) : 0
  const indeterminate = !checked && coveredChildren > 0 && coveredChildren < childPaths.length

  useEffect(() => {
    if (checkboxRef.current) checkboxRef.current.indeterminate = indeterminate
  }, [indeterminate])

  const handleToggle = () => {
    if (checked) {
      // odeber tuto cestu i všechny cesty pod ní
      onChange(selected.filter(sel => !(sel.length >= path.length && path.every((v, i) => v === sel[i]))))
    } else {
      // přidej tuto cestu, odeber nadřazené (prefix path) i podřazené (path je prefix) cesty téže větve
      const pruned = selected.filter(sel => {
        const isAncestor = sel.length <= path.length && sel.every((v, i) => v === path[i])
        const isDescendant = sel.length > path.length && path.every((v, i) => v === sel[i])
        return !isAncestor && !isDescendant
      })
      onChange([...pruned, path])
    }
  }

  const count = node?.ghIds?.size

  return (
    <div>
      <div className="flex items-center gap-1 py-0.5 px-1 hover:bg-gray-50 rounded text-xs">
        {hasChildren ? (
          <button onClick={() => setExpanded(v => !v)} className="w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600 shrink-0">
            {expanded ? '▼' : '▶'}
          </button>
        ) : (
          <div className="w-4 shrink-0" />
        )}

        <input
          ref={checkboxRef}
          type="checkbox"
          checked={checked}
          onChange={handleToggle}
          className="accent-blue-500 cursor-pointer w-3.5 h-3.5 shrink-0"
        />

        <span className="flex-1 cursor-pointer hover:font-medium" onClick={handleToggle}>
          {label}
          {count != null && <span className="text-gray-400"> ({count})</span>}
        </span>
      </div>

      {expanded && hasChildren && (
        <div className="ml-2 border-l border-gray-100 pl-1">
          {childrenNodes.map((child, idx) => (
            <GroupNode
              key={`${idx}-${child.label}`}
              label={child.label}
              node={child}
              path={[...path, child.label]}
              childrenNodes={child.children}
              selected={selected}
              onChange={onChange}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export function GroupCheckboxFilter({ trees, selected = [], onChange }) {
  if (!trees || trees.length === 0) return null
  return (
    <div className="flex flex-col gap-1.5 max-h-96 overflow-y-auto text-xs">
      {trees.map(({ id, name, tree }) => (
        <GroupNode
          key={id}
          label={name}
          node={null}
          path={[id]}
          childrenNodes={tree}
          selected={selected}
          onChange={onChange}
          defaultExpanded={trees.length === 1}
        />
      ))}
    </div>
  )
}
